import { AuthUser } from '@aws-amplify/auth';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { SmsConsentStatus } from '@chiroup/core/enums/SmsConsentStatus.enum';
import { convertToMS } from '@chiroup/core/functions/convertToMS';
import { scrollToId } from '@chiroup/core/functions/scrollToId';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import { Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import appointmentService from '../services/appointment.service';
import locationService, {
  type AvailabilityResponse,
  type AvailabilitySlot,
} from '../services/location.service';
import smsOptinService from '../services/smsOptin.service';
import { ScheduleForm } from './Container';
import NotSetup from './NotSetup';
import ScheduleConfirm from './ScheduleConfirm';
import ScheduleProviders from './ScheduleProviders';
import ScheduleTime from './ScheduleTime';
import ScheduleTreatments from './ScheduleTreatments';

dayjs.extend(utc);
dayjs.extend(tz);

const today = dayjs();
const startOfMonth = today.startOf('month');
const normalizePhone = (phone?: string | null) => (phone || '').replace(/\D/g, '');

interface AvailableTimesType {
  providerId: string;
  providerName?: string;
  profileImage?: string | null;
  times: AvailabilitySlot[];
}

type Props = {
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
  user: AuthUser | null;
  clinicLocationData?: HealthComLocation;
  path: string;
  getMe: () => Promise<void>;
  values: ScheduleForm;
  setValues: React.Dispatch<React.SetStateAction<ScheduleForm>>;
  me: {
    patients: any[];
  } | null;
};

const Schedule: React.FC<Props> = ({
  setUser,
  user: _user,
  clinicLocationData,
  path,
  getMe,
  me,
  values,
  setValues,
}) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [viewDate, setViewDate] = useState(startOfMonth);
  const [isCreditCardRequired, setIsCreditCardRequired] = useState(false);
  const [epayPublicKey, setEpayPublicKey] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityResponse>();
  const [dottedDays, setDottedDays] = useState<{
    [key: number]: boolean;
  }>({});
  const [availableTimes, setAvailableTimes] = useState<AvailableTimesType[]>([]);
  const [selectedDay, setSelectedDay] = useState<string>();
  const [loadingAvailability, setLoadingAvailability] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [appointmentSettings, setAppointmentSettings] = useState({
    requireCreditCard: false,
  });

  useEffect(() => {
    if (me?.patients?.length === 1) {
      setValues(prev => ({
        ...prev,
        patient: me?.patients[0]?.ID,
      }));
    }
  }, [me?.patients, setValues]);

  const patientSelected = useMemo(() => {
    if (values.patient) {
      return me?.patients?.find(p => p.ID === values.patient);
    }

    return null;
  }, [values.patient, me?.patients]);

  useEffect(() => {
    if (!values.patient || !patientSelected) return;

    if (patientSelected.phone && values.phone !== patientSelected.phone) {
      setValues(prev => ({
        ...prev,
        phone: patientSelected.phone,
      }));
    }

    if (
      patientSelected.smsConsent &&
      (values.smsConsent === null || values.smsConsent === undefined)
    ) {
      setValues(prev => ({
        ...prev,
        smsConsent: patientSelected.smsConsent,
      }));
    }
  }, [patientSelected, setValues, values.patient, values.phone, values.smsConsent]);

  const selectedPatientHasPaymentMethod = useMemo(() => {
    return !!patientSelected?.hasPaymentMethod;
  }, [patientSelected]);

  useEffect(() => {
    if (!appointmentSettings?.requireCreditCard) {
      setIsCreditCardRequired(false);
    } else {
      setIsCreditCardRequired(!selectedPatientHasPaymentMethod);
    }
  }, [selectedPatientHasPaymentMethod, appointmentSettings?.requireCreditCard, patientSelected]);

  const getAvailability = async (
    provider: string | null,
    treatment: number | null,
    year: string,
    month: string
  ) => {
    setLoadingAvailability(true);
    const { formattedAvailability, appointmentSettings: fetchedSettings } =
      await locationService.getAvailability({
        path,
        provider,
        treatment,
        year,
        month,
        timezone: clinicLocationData?.timezone as string,
      });

    if (fetchedSettings?.epayPublicKey) {
      setEpayPublicKey(fetchedSettings?.epayPublicKey);
    }

    setIsCreditCardRequired(!!fetchedSettings?.requireCreditCard);
    setAppointmentSettings(fetchedSettings);

    setAvailability(formattedAvailability);
    setDottedDays(
      Object.entries(formattedAvailability).reduce(
        (
          acc: {
            [key: number]: boolean;
          },
          [date, dayAvailability]
        ) => {
          const day = dayjs(date).date();
          const isAvailable = Object.values(dayAvailability).some(times => times.length);

          if (isAvailable) {
            acc[day] = true;
          }

          return acc;
        },
        {}
      )
    );

    setLoadingAvailability(false);
  };

  const confirm = async () => {
    try {
      /**
       * This provides a little extra information if the email or the phone number
       * are missing. HC login will be email or phone number, so we can use that if
       * the form values happen to not have it.
       */
      // const u = user?.username;
      // const isEmail = /@/.test(u as string);
      const submitThis: ScheduleForm = { ...values };

      // if (isEmail) {
      //   submitThis.email = submitThis.email || u;
      // } else {
      //   submitThis.phone = submitThis.phone || u;
      // }

      setError(null);
      setIsSaving(true);

      try {
        if (
          submitThis.phone &&
          submitThis.smsConsent === SmsConsentStatus.OPTED_IN &&
          !hasPhoneConsent
        ) {
          await smsOptinService.optInTransactional({
            clinicPath: path,
            phoneNumber: submitThis.phone,
          });
        }

        if (isCreditCardRequired) {
          if ((window as any).usaepay) {
            const client = new (window as any).usaepay.Client(epayPublicKey);
            const paymentCard = client.createPaymentCardEntry();

            const result = await client.getPaymentKey(paymentCard);

            if (result.error) {
              console.warn('CCerror', result.error);
              throw new Error(result.error);
            } else {
              await appointmentService.create(
                {
                  ...submitThis,
                  path,
                },
                result.key
              );
            }
          }
        } else {
          await appointmentService.create({
            ...submitThis,
            path,
          });
        }

        if (clinicLocationData?.googleAnalyticsTrackingId) {
          ReactGA.event({
            category: 'scheduling',
            action: 'scheduled_appointment',
          });
        }

        await getMe();
        setValues((prev: any) => ({
          ...prev,
          date: null,
          startTime: null,
          endTime: null,
          treatment: null,
          provider: null,
          time: null,
        }));
        navigate(`/${path}/thank-you`);
      } catch (e: any) {
        const err = typeof e === 'string' ? JSON.parse(e) : e;
        setError(err.response?.data?.code || err.response?.data?.message || err.message);
      }

      setIsSaving(false);
    } catch (err: any) {
      setIsSaving(false);
      console.error({ err });
      setError(err.response?.data?.code);
    }
  };

  const providerSelected = useMemo(() => {
    return (
      clinicLocationData?.providers?.find(p => p.ID === values.provider) || {
        name: '',
        profileImage: '',
      }
    );
  }, [clinicLocationData?.providers, values.provider]);

  const allTreatments = useMemo(() => {
    return clinicLocationData?.disciplines?.reduce((acc: any[], discipline: any) => {
      return [...acc, ...discipline.treatments];
    }, []);
  }, [clinicLocationData?.disciplines]);

  const treatmentName = useMemo(() => {
    for (const discipline of clinicLocationData?.disciplines || []) {
      for (const treatment of discipline.treatments) {
        if (treatment.ID === values.treatment) return treatment.name;
      }
    }

    return '';
  }, [clinicLocationData?.disciplines, values.treatment]);

  const appointmentTime = useMemo(() => {
    return values.startTime && values.endTime
      ? `${dayjs
          .tz(convertToMS(Number(values.startTime)), clinicLocationData?.timezone)
          .format('h:mma')} - ${dayjs
          .tz(convertToMS(Number(values.endTime)), clinicLocationData?.timezone)
          .format('h:mma')}`
      : '';
  }, [clinicLocationData?.timezone, values.endTime, values.startTime]);

  const hasPhoneConsent = useMemo(() => {
    if (!values.phone || !me?.patients?.length) return false;

    const normalized = normalizePhone(values.phone);

    return me.patients.some(
      (p: any) =>
        normalizePhone(p?.phone) === normalized && p?.smsConsent === SmsConsentStatus.OPTED_IN
    );
  }, [values.phone, me?.patients]);

  const providerInfo = (userId: string) => {
    return clinicLocationData?.providers?.find((p: any) => p.ID === userId);
  };

  const providerOptions = useMemo(() => {
    const treatmentSelected = allTreatments?.find(t => t.ID === values.treatment);
    if (!treatmentSelected?.providers?.length) return clinicLocationData?.providers || [];

    return (
      clinicLocationData?.providers?.filter(p => {
        return treatmentSelected.providers.includes(p.ID);
      }) || []
    );
  }, [clinicLocationData?.providers, values.treatment, allTreatments]);

  const cannotBook = useMemo(() => {
    return !providerOptions.length || !allTreatments?.length;
  }, [providerOptions, allTreatments]);

  return cannotBook ? (
    <NotSetup />
  ) : clinicLocationData ? (
    <div className="mx-auto max-w-5xl flex flex-col gap-12 sm:gap-24">
      <div className="flex flex-col gap-4">
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold text-gray-900 sm:truncate sm:text-3xl">
              Schedule Appointment
            </h2>
          </div>
        </div>
        <Transition
          show={!!values.startTime}
          enter="transition-opacity ease-linear duration-300 delay-700"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ScheduleConfirm
            values={values}
            setValues={setValues}
            onChange={(key: string) => (value: string) => {
              setValues((prev: any) => ({
                ...prev,
                [key]: value,
              }));
            }}
            treatmentName={treatmentName}
            providerName={providerSelected?.name || ''}
            providerImage={providerSelected?.profileImage || ''}
            appointmentDate={dayjs
              .tz(values?.date, clinicLocationData?.timezone)
              .format('MMMM D, YYYY')}
            appointmentTime={appointmentTime}
            makeChanges={() => {
              setError(null);
              setAvailability({});
              setValues((prev: any) => ({
                ...prev,
                date: null,
                startTime: null,
                endTime: null,
              }));
              scrollToId('time');
            }}
            setUser={setUser}
            path={path}
            getMe={getMe}
            me={me}
            confirm={confirm}
            isSaving={isSaving}
            error={error}
            locationData={clinicLocationData}
            isCreditCardRequired={isCreditCardRequired}
            epayPublicKey={epayPublicKey}
            hasPhoneConsent={hasPhoneConsent}
          />
        </Transition>
        <Transition
          show={!values.startTime}
          enter="transition-opacity ease-linear duration-300 delay-700"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ScheduleTreatments
            disciplines={clinicLocationData?.disciplines}
            value={values.treatment}
            onChange={(treatment: number) => {
              setAvailability({});
              setValues((prev: any) => ({
                ...prev,
                treatment,
                provider: null,
                date: null,
                startTime: null,
                endTime: null,
              }));
              scrollToId('providers');
            }}
            providerOptions={providerOptions}
          />
          {!!values.treatment && (
            <ScheduleProviders
              providers={providerOptions}
              onChange={async (provider: string) => {
                setAvailability({});
                setAvailableTimes([]);
                setValues((prev: any) => ({
                  ...prev,
                  provider,
                  date: null,
                  startTime: null,
                  endTime: null,
                }));
                scrollToId('time');
                await getAvailability(
                  provider,
                  values.treatment,
                  viewDate.format('YYYY'),
                  viewDate.format('MM')
                );
              }}
              value={values.provider}
            />
          )}
          {!!values.provider && (
            <ScheduleTime
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              className="mt-12"
              onChange={(val: any) => {
                setValues((prev: any) => ({
                  ...prev,
                  ...val,
                }));
              }}
              primaryColor={clinicLocationData?.primaryColor}
              onSelectDate={(date?: string) => {
                if (!date) return setAvailableTimes([]);

                const availabilityForDay = availability?.[date];

                const availabilityForProvider =
                  values?.provider && values?.provider !== 'no-preference'
                    ? [
                        {
                          providerId: values?.provider,
                          providerName: providerInfo(values?.provider)?.name,
                          profileImage: providerInfo(values?.provider)?.profileImage,
                          times: availabilityForDay?.[values?.provider] || [],
                        },
                      ]
                    : Object.entries(availabilityForDay || {}).map(([provider, times]) => ({
                        providerId: provider,
                        providerName: providerInfo(provider)?.name,
                        profileImage: providerInfo(provider)?.profileImage,
                        times: times || [],
                      }));
                console.log({ availabilityForProvider });
                setAvailableTimes(availabilityForProvider);
                setSelectedDay(dayjs(date).format('MMMM D, YYYY'));
                scrollToId('time');
              }}
              selectedDay={selectedDay}
              onChangeMonth={(year: string, month: string) => {
                getAvailability(values.provider, values.treatment, year, month);
              }}
              dottedDays={dottedDays}
              loading={loadingAvailability}
              availableTimes={availableTimes}
              setViewDate={setViewDate}
              viewDate={viewDate}
            />
          )}
        </Transition>
      </div>
    </div>
  ) : (
    <LoadingPage />
  );
};

export default Schedule;
